import type { MaskInputOptions, MaskOptions, MaskType } from "maska";
import type Input from "@/components/ui/input/Input";
import { MaskInput } from "maska";

import { onMounted, onUnmounted, ref } from "vue";

export const masks = {
  password: withRegex(/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/),
  name: withRegex(/^[a-zA-ZÀ-ÿ\u00f1\u00d1\s\-.'&]/),
  company_name: withRegex(/^[a-zA-ZÀ-ÿ\u00f1\u00d10-9\s\-.'&]/),
  function: withRegex(/^[a-zA-Z0-9-_]/),
  username: withRegex(/^[a-zA-Z0-9@.\-_]/, (char) => char.toLowerCase()),
  email: withRegex(/^[a-zA-Z0-9@._]/, (char) => char.toLowerCase()),
  url: withRegex(/[0-9a-zA-Z._\-:/]/, (char) => char.toLowerCase()),
  ip: ipV4Mask(),
  phone: withRegex(/[0-9+()\- ]/),
  digits: withRegex(/[0-9.,]/),
  snakeCase: withRegex(/[0-9a-zA-Z_]/, (char) => char.toLowerCase()),
  brl: {
    mask: "R$ #*",
    tokens: {
      "#": {
        pattern: /[0-9.,]/,
        optional: true,
        multiple: true,
      },
    },
  },
  percentage: {
    mask: "#%",
    tokens: {
      "#": {
        pattern: /[0-9,.]/,
        optional: true,
        multiple: true,
      },
    },
  },
  promo_code: {
    mask: "HHHHHHHHHH",
    tokens: {
      H: {
        pattern: /[a-zA-Z0-9]/,
        optional: false,
        multiple: true,
        transform(char) {
          return char.toUpperCase();
        },
      },
    },
  },
} satisfies Record<string, MaskInputOptions>;

export type MaskName = keyof typeof masks;

export function withRegex(regex: RegExp, transform?: (char: string) => string): MaskOptions {
  return {
    mask: "A",
    tokens: {
      A: {
        pattern: regex,
        transform,
        multiple: true,
      },
    },
  };
}

function ipV4Mask(): MaskOptions {
  return {
    mask: "#00.#00.#00.#00",
    tokens: {
      0: {
        pattern: /[0-9]/,
        optional: true,
      },
    },
  };
}

export const useMask = (props: { mask?: MaskOptions | MaskType | RegExp }) => {
  const inputRef = ref<InstanceType<typeof Input> | HTMLInputElement>();
  let mask: MaskInput;

  onMounted(() => {
    if (props.mask) {
      const el = getEl(inputRef.value);

      if (!el) return;

      if (props.mask instanceof RegExp) {
        mask = new MaskInput(el, withRegex(props.mask));
        return;
      }

      if (typeof props.mask === "string" && props.mask in masks) {
        mask = new MaskInput(el, masks[props.mask as keyof typeof masks]);
        return;
      }

      if (isMaskOptions(props.mask)) {
        mask = new MaskInput(el, props.mask);
        return;
      } else {
        if (typeof props.mask === "string" && isRegex(props.mask)) {
          mask = new MaskInput(
            el,
            withRegex(new RegExp(props.mask.substring(1, props.mask.length - 1)))
          );
        } else {
          mask = new MaskInput(el, {
            mask: props.mask,
          });
        }
      }
    }
  });

  onUnmounted(() => {
    mask?.destroy();
  });

  return inputRef;
};

function isRegex(value: string) {
  return value.startsWith("/") && value.endsWith("/");
}

export function isMaskOptions(mask: MaskOptions | MaskType): mask is MaskOptions {
  return typeof mask === "object" && !Array.isArray(mask);
}

function getEl(el: InstanceType<any> | HTMLInputElement) {
  if (el instanceof HTMLInputElement) return el;
  if (el.$el instanceof HTMLInputElement) return el.$el;
  return el.$el.querySelector("input");
}
